/**
 * How long a hold looks like it has left.
 *
 * Presentation, and only presentation. The customer's device decides what the
 * countdown says; the server decides whether the hold can still be turned into
 * a purchase. Those are different questions and a browser is qualified to
 * answer exactly one of them — a laptop whose clock is two minutes fast would
 * otherwise tell somebody their seats were gone while the server was still
 * perfectly willing to sell them.
 *
 * So nothing here refuses anything. Checkout is still attempted when this says
 * the time is up, and the answer comes back from the authority.
 */
export interface HoldClock {
  /** Milliseconds left by this device's reckoning, floored at zero. */
  readonly remainingMs: number;
  /** True once this device's clock has passed the deadline. */
  readonly lapsed: boolean;
  /** "4:07", or "0:00" once lapsed. Minutes and seconds, never a bare count. */
  readonly remaining: string;
  /** What to tell the customer, in words rather than only a number. */
  readonly meaning: string;
}

const LAPSED =
  'The time on these seats has run out. Checking out will ask GateCheckPro whether they are still yours.';

export function holdClock(expiresAt: string, now: number): HoldClock {
  const deadline = Date.parse(expiresAt);
  // An unparseable deadline is not a lapsed one. Treating it as expired would
  // take a working hold away from somebody over a formatting problem.
  if (Number.isNaN(deadline)) {
    return {
      remainingMs: 0,
      lapsed: false,
      remaining: '—',
      meaning: 'These seats are being held for you. GateCheckPro is keeping the time.',
    };
  }

  const remainingMs = Math.max(0, deadline - now);
  if (remainingMs === 0) {
    return { remainingMs: 0, lapsed: true, remaining: '0:00', meaning: LAPSED };
  }

  const totalSeconds = Math.ceil(remainingMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    remainingMs,
    lapsed: false,
    remaining: `${String(minutes)}:${String(seconds).padStart(2, '0')}`,
    meaning:
      'These seats are held for you while you check out. Nobody else can buy them until the time runs out.',
  };
}
